/**
 * ADR-0220 §3.3 — New initiative (`/app/initiatives/new`; builders and admins).
 * A create screen keeps an explicit submit. Born proposed; the measure and the
 * relations are set on the detail page. Owners default to the creator and are
 * never empty; Teams are the workspace's groups (the teams amendment).
 */

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";

type Errors = Partial<Record<"name" | "goal" | "description" | "owners" | "dates", string>>;

/** The checks a field gets when the person leaves it (UI_GUIDELINES §1.6), and all of them on submit. */
export function validateCreate(form: { name: string; goal: string; description: string; owners: string[]; planned_start: string; planned_end: string }): Errors {
  const e: Errors = {};
  if (!form.name.trim()) e.name = "Name is required.";
  else if (form.name.trim().length > 200) e.name = "At most 200 characters.";
  if (!form.goal.trim()) e.goal = "Goal is required.";
  else if (form.goal.trim().length > 2000) e.goal = "At most 2000 characters.";
  if (form.description.trim().length > 20000) e.description = "At most 20000 characters.";
  if (form.owners.length === 0) e.owners = "An initiative keeps at least one owner.";
  if (form.planned_start && form.planned_end && form.planned_end < form.planned_start) e.dates = "The planned end is before the planned start.";
  return e;
}

/** ADR-0223 §3.3: the idea an initiative is created from, carried in location state by *Make it → Initiative*; anything else is ignored. */
export function fromIdeaState(state: unknown): { id: string; title: string; description: string } | null {
  const s = state as { from_idea?: { id?: unknown; title?: unknown; description?: unknown } } | null;
  const f = s?.from_idea;
  if (!f || typeof f.id !== "string" || typeof f.title !== "string") return null;
  return { id: f.id, title: f.title, description: typeof f.description === "string" ? f.description : "" };
}

export function TenantInitiativeCreate(props: { callerAccountId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const fromIdea = fromIdeaState(useLocation().state);
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState(fromIdea?.title ?? "");
  const [goal, setGoal] = useState(fromIdea?.description ?? "");
  const [description, setDescription] = useState("");
  const [owners, setOwners] = useState<string[]>([props.callerAccountId]);
  const [teams, setTeams] = useState<string[]>([]);
  const [sponsor, setSponsor] = useState("");
  const [plannedStart, setPlannedStart] = useState("");
  const [plannedEnd, setPlannedEnd] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates")
      .then((r) => setCandidates(r.candidates))
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  const form = { name, goal, description, owners, planned_start: plannedStart, planned_end: plannedEnd };
  const check = (field: keyof Errors) => setErrors((prev) => ({ ...prev, [field]: validateCreate(form)[field] }));

  async function submit(): Promise<void> {
    const found = validateCreate(form);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = await apiFetch<{ initiative: { id: string } }>("/api/tenant/initiatives", {
        method: "POST",
        body: {
          ...(fromIdea ? { from_idea_id: fromIdea.id } : {}),
          name: name.trim(),
          goal: goal.trim(),
          description: description.trim() || null,
          owner_account_ids: owners,
          team_group_ids: teams,
          sponsor_account_id: sponsor || null,
          planned_start: plannedStart || null,
          planned_end: plannedEnd || null,
        },
      });
      toast.push({ kind: "success", message: "Initiative created." });
      navigate(`/app/initiatives/${encodeURIComponent(r.initiative.id)}`);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const people = candidates.filter((c) => c.kind === "user");
  const danger = { color: "var(--danger-text)" };

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <Link className="breadcrumbLink" to="/app/initiatives">
            Initiatives
          </Link>
          <span className="breadcrumbSep">/</span>
          <span>New initiative</span>
        </span>
      }
    >
      {loadError ? <ErrorState detail={loadError} /> : null}
      {!loadError && loading ? <LoadingState /> : null}
      {!loadError && !loading ? (
        <div className="card" style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">Name *</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => check("name")} />
            {errors.name ? <div className="help" style={danger}>{errors.name}</div> : null}
          </label>
          <label className="field">
            <span className="label">Goal *</span>
            <textarea className="input" rows={3} value={goal} onChange={(e) => setGoal(e.target.value)} onBlur={() => check("goal")} />
            <div className="help" style={errors.goal ? danger : undefined}>
              {errors.goal ?? "One or two sentences: the result the business commits to, and how you will know it happened."}
            </div>
          </label>
          <label className="field">
            <span className="label">Description</span>
            <textarea className="input initMarkdown" value={description} onChange={(e) => setDescription(e.target.value)} onBlur={() => check("description")} />
            <div className="help" style={errors.description ? danger : undefined}>
              {errors.description ?? "Optional. Markdown: background, scope, approach."}
            </div>
          </label>
          <div className="field">
            <span className="label">Owners *</span>
            <MemberPicker
              mode="multi"
              allowedKinds={["user"]}
              candidates={candidates}
              selected={owners}
              onChange={(next) => {
                setOwners(next);
                setErrors((prev) => ({ ...prev, owners: next.length === 0 ? "An initiative keeps at least one owner." : undefined }));
              }}
              placeholder="Add an owner…"
            />
            {errors.owners ? <div className="help" style={danger}>{errors.owners}</div> : null}
          </div>
          <div className="field">
            <span className="label">Teams</span>
            <MemberPicker mode="multi" allowedKinds={["group"]} candidates={candidates} selected={teams} onChange={setTeams} placeholder="Add a team…" />
          </div>
          <label className="field">
            <span className="label">Sponsor</span>
            <select className="select" value={sponsor} onChange={(e) => setSponsor(e.target.value)}>
              <option value="">None</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </label>
          <div className="inputRow">
            <label className="field">
              <span className="label">Planned start</span>
              <input className="input" type="date" value={plannedStart} onChange={(e) => setPlannedStart(e.target.value)} onBlur={() => check("dates")} />
            </label>
            <label className="field">
              <span className="label">Planned end</span>
              <input className="input" type="date" value={plannedEnd} onChange={(e) => setPlannedEnd(e.target.value)} onBlur={() => check("dates")} />
            </label>
          </div>
          {errors.dates ? <div className="help" style={danger}>{errors.dates}</div> : null}
          {saveError ? <div className="help" style={danger}>{saveError}</div> : null}
          <div className="actions">
            <button type="button" className="btn btnPrimary" disabled={busy} onClick={() => void submit()}>
              {busy ? "Creating…" : "Create initiative"}
            </button>
            <Link className="btn" to="/app/initiatives">
              Cancel
            </Link>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
